export type TableCell = {
  html: string;
  text: string;
};

export type ParsedTable = {
  headers: TableCell[];
  rows: TableCell[][];
};

function cellToTableCell(cell: HTMLTableCellElement): TableCell {
  return {
    html: cell.innerHTML,
    text: (cell.textContent ?? '').trim(),
  };
}

/**
 * Reads a rendered mkdocs `<table>` into plain data, preserving each cell's
 * original HTML (so links, code spans, etc. authored in the source markdown
 * keep working) alongside its plain text (used for sorting/searching).
 */
export function parseTable(table: HTMLTableElement): ParsedTable {
  // table.rows is a live collection of every row in document order
  // (thead + tbody + tfoot combined) - filtering the header row out of it
  // by reference, rather than assuming a tbody exists, avoids double-
  // counting the header row when a table has no explicit <thead>: browsers
  // implicitly wrap ALL un-sectioned rows (including what we're treating as
  // the header) in a single <tbody>.
  const allRows = Array.from(table.rows);
  const headerRow = table.tHead?.rows[0] ?? allRows[0] ?? null;
  const headers = headerRow
    ? Array.from(headerRow.cells).map(cellToTableCell)
    : [];

  const bodyRows = allRows.filter(row => row !== headerRow);
  const rows = bodyRows.map(row => Array.from(row.cells).map(cellToTableCell));

  return { headers, rows };
}
